import { observer } from 'mobx-react-lite';
import type { ReactNode } from 'react';
import { useLocation } from 'react-router';

import { tableStore } from '../stores/table-store';
import {
  useProStudioCtxSelector,
  useProStudioStateMatches,
  useUserCtxSelector,
} from '../xstate/app.xstate';
import { buildCollabUrl } from '../utils/collab-url';
import { collabDraftIdentity } from './collab-draft-identity';
import { canFeedFromCollab } from './collab-live-source';
import { isWorkflowsPath, solutionIdInPath } from './collab-routes';
import { CollabSessionContext } from './collab-session-context';
import { useCollabSession } from './use-collab-session';
import {
  useCollabPresenceShare,
  useCollabTableFeed,
} from './use-collab-table-feed';

/**
 * The open draft's live collab session, held ABOVE the routes (PSTUDIO-4449).
 *
 * The session's store owns the draft's undo history (the client's Yjs
 * UndoManager). Opened by the Workflows page it died with that page, so a trip
 * to Files and back — or browser Back/Forward — came back with nothing to
 * undo, where the Hasura-backed table kept its history. Mounted in the app
 * shell, which never unmounts between pages, it lives as long as the user is
 * on the draft's solution:
 *
 * - moving between that solution's pages keeps it;
 * - the address naming any other place — All Solutions, Project Settings,
 *   another solution — closes it, without waiting for the machine to catch up;
 * - a new draft (a publish that rolls the draft over) opens a new one.
 *
 * It also feeds the table from the document on every page of the solution, as
 * the Hasura path fed it on every page: Publish is pressed on Versions as much
 * as on Workflows, and opened there directly (a reload, a link) the table
 * otherwise waited on Hasura for a v2 draft it does not hold — Publish never
 * enabled. Only the Workflows page shares a cursor, on its own lifetime, so
 * leaving it clears the selection coworkers see without restarting the feed.
 */
export const CollabSessionHost = observer(
  ({ children }: { children: ReactNode }) => {
    const { pathname } = useLocation();
    const routeSolutionId = solutionIdInPath(pathname);
    // The machine's fields only: `useCtxSelector` re-runs a selector on a
    // machine EVENT, never because the route it closed over changed, so the
    // route is joined in the render below rather than inside the selector.
    const { uniqueId, hasSelectedBot, draftKey, draftOwner } =
      useProStudioCtxSelector((ctx) => ({
        // Primitives, so the selector's shallow compare holds.
        uniqueId: ctx.selectedBot?.uniqueId,
        hasSelectedBot: !!ctx.selectedBot,
        // The key the table store is on (see use-table-store-sync).
        draftKey: ctx.solutionVersionPsId ?? '',
        draftOwner: ctx.solutionVersionPsSolutionId,
      }));
    // Security review: a stale context must not open the draft's socket
    // behind the access-denied screen.
    const isAccessDenied = useProStudioStateMatches('accessDenied');
    const draft = collabDraftIdentity({
      selectedBot: hasSelectedBot ? { uniqueId } : undefined,
      routeSolutionId,
      solutionVersionPsId: draftKey,
      solutionVersionPsSolutionId: draftOwner,
    });
    const onIt =
      !isAccessDenied && !!draft && draft.solutionId === routeSolutionId;
    const solutionId = onIt ? draft.solutionId : undefined;
    const solutionVersionId = onIt ? draft.solutionVersionId : undefined;
    const userId = useUserCtxSelector((ctx) => ctx.user?.id);
    const url = buildCollabUrl(import.meta.env.FE_ADMIN_URL);

    const session = useCollabSession({
      url,
      solutionId,
      solutionVersionId,
      userId,
    });

    // A legacy draft keeps loading from Hasura.
    const liveSource = canFeedFromCollab({ url, solutionVersionId: draftKey });
    const liveStore = liveSource ? session.store : null;
    useCollabTableFeed({
      store: liveStore,
      solutionVersionId,
      tableSolutionVersionId: tableStore.solutionVersionId,
      visit: tableStore.versionRevisitRequests,
      table: tableStore,
    });
    useCollabPresenceShare({
      store: liveStore,
      table: tableStore,
      enabled: isWorkflowsPath(pathname),
    });

    return (
      <CollabSessionContext.Provider value={session}>
        {children}
      </CollabSessionContext.Provider>
    );
  }
);
